"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { ChevronDown, FileUp, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { track } from "@vercel/analytics";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResumeUploadError, resumeUploadHref, RESUME_UPLOAD_TTL_MS } from "@/lib/domain/resume-upload";
import { clearExpiredResumeUpload, stageResumeUpload } from "@/lib/infra/pending-resume-upload";

export default function HeroResumeUpload() {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn } = useUser();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const busyRef = useRef(false);
  const helpId = useId();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const cleanup = () => { void clearExpiredResumeUpload().catch(() => undefined); };
    cleanup();
    const timer = setInterval(cleanup, RESUME_UPLOAD_TTL_MS);
    return () => clearInterval(timer);
  }, []);

  const attachResume = async (file: File) => {
    if (!isLoaded || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setDragging(false);
    try {
      const id = await stageResumeUpload(file);
      track("hero_resume_selected", { signedIn: Boolean(isSignedIn) });
      router.push(resumeUploadHref(isSignedIn ? "/import" : "/sign-up", id));
    } catch (cause) {
      const key = cause instanceof ResumeUploadError
        ? cause.messageKey
        : "HOME_RESUME_UPLOAD_STORAGE_ERROR";
      setError(t(key));
      track("hero_resume_upload_error", { code: key });
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-fit max-w-full">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        aria-label={t("HOME_RESUME_UPLOAD_BUTTON")}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void attachResume(file);
        }}
      />
      <Popover open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); setDragging(false); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={!isLoaded || busy}
            className="inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-slate-300 bg-white/55 px-4 text-xs font-semibold text-indigo-700 backdrop-blur-xl transition hover:border-indigo-300 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 dark:border-white/20 dark:bg-slate-950/35 dark:text-sky-300 dark:hover:bg-white/5 sm:text-sm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileUp className="h-4 w-4" aria-hidden="true" />}
            <span>{t(busy ? "HOME_RESUME_UPLOAD_LOADING" : "HOME_RESUME_UPLOAD_BUTTON")}</span>
            <ChevronDown className={cn("h-4 w-4 text-slate-500 transition-transform dark:text-slate-400", open && "rotate-180")} aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          aria-label={t("HOME_RESUME_UPLOAD_BUTTON")}
          sideOffset={8}
          className="w-80 max-w-[calc(100vw-2rem)] rounded-2xl border-slate-200 bg-gradient-to-b from-slate-50 to-white p-3 shadow-xl dark:border-white/15 dark:from-slate-900 dark:to-slate-950"
        >
          <button
            type="button"
            disabled={!isLoaded || busy}
            onClick={() => inputRef.current?.click()}
            aria-describedby={helpId}
            className={cn("flex min-h-28 w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-5 text-center transition hover:border-indigo-400 hover:bg-indigo-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-wait disabled:opacity-60 dark:border-white/20 dark:hover:bg-white/5", dragging && "border-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/50")}
            onDragOver={(event) => {
              event.preventDefault();
              if (!busy && isLoaded) setDragging(true);
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files[0];
              if (file) void attachResume(file);
            }}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin text-indigo-600 dark:text-sky-300" aria-hidden="true" /> : <FileUp className="h-5 w-5 text-indigo-600 dark:text-sky-300" aria-hidden="true" />}
            <span className="text-sm font-semibold text-slate-900 dark:text-white">{t(busy ? "HOME_RESUME_UPLOAD_LOADING" : "HOME_RESUME_UPLOAD_CHOOSE")}</span>
            <span id={helpId} className="text-xs leading-5 text-slate-500 dark:text-slate-400">{t("HOME_RESUME_UPLOAD_HELP")}</span>
          </button>
          {error && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>}
        </PopoverContent>
      </Popover>
    </div>
  );
}
